/**
 * useTenantBilling — Hooks do novo hub "Minha assinatura" (/tenant/plans)
 *
 * Disponibiliza:
 *  - useTenantInvoices(filters): lista paginada de faturas do tenant
 *  - useTenantAddons(): lista de add-ons (recorrências adicionais) contratados
 *
 * Todas as chamadas passam por RPCs SECURITY DEFINER criadas em
 * `tenant_billing_list_invoices` / `tenant_billing_list_addons` (2026-06-15).
 */
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { createLogger } from "@/lib/logger";
import type { CobrancaMemoriaCalculo } from "@/hooks/financial/useCobrancaCalculationMemory";

// ---------------------------------------------------------------------------
// Realtime: sincroniza a Visão Geral de Planos quando tenant_addons muda
// ---------------------------------------------------------------------------
/**
 * useRealtimeTenantBillingSync
 *
 * Assina mudanças (INSERT/UPDATE/DELETE) na tabela `public.tenant_addons`
 * filtradas pelo tenant ativo. Em qualquer evento, invalida o cache das
 * queries do hub "Minha assinatura" para refletir o estado atualizado
 * imediatamente (sem precisar de F5).
 *
 * Justificativa: o trigger backend `trg_sync_tenant_billing_on_charge`
 * dispara `tenant_addons_reconcile_from_assinatura` ao confirmar pagamento,
 * o que atualiza `paid_quantity`/`pending_quantity` em `tenant_addons`.
 * Assinando essa tabela cobrimos o caso sem duplicar o listener de
 * `financeiro_cobrancas` (já mantido nas páginas do módulo Financeiro).
 */
export function useRealtimeTenantBillingSync(tenantId?: string | null) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!tenantId) return;

    // Nome único por tenant para evitar colisão de canais entre montagens
    const channel = supabase
      .channel(`tenant-addons-rt-${tenantId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tenant_addons",
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          log.debug("Realtime tenant_addons", { event: payload.eventType });
          invalidateTenantBillingFreshness(queryClient);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenantId, queryClient]);
}


const log = createLogger("useTenantBilling");

// ---------------------------------------------------------------------------
// Cache: chaves que compõem o estado fresco de assinatura/add-ons do tenant
// ---------------------------------------------------------------------------
/**
 * Lista centralizada dos caches impactados por pagamento/reconciliação de
 * add-ons. Mantém o refresh consistente entre Hub, aba Catálogo, Visão Geral e
 * fluxos de pagamento por cartão salvo.
 */
export const TENANT_BILLING_FRESHNESS_KEYS = [
  ["tenant-billing-self-overview"],
  ["tenant-billing-invoices"],
  ["tenant-billing-addons"],
  ["tenant-eligible-plans-v1"],
  ["tenant-eligible-addons-v1"],
  ["resource-limits"],
  ["tenant-addons"],
  ["tenant-instance-quota"],
  ["tasks-usage"],
] as const;

/** Invalida todos os caches que podem refletir add-ons/cotas pós-pagamento. */
export function invalidateTenantBillingFreshness(queryClient: QueryClient) {
  for (const key of TENANT_BILLING_FRESHNESS_KEYS) {
    queryClient.invalidateQueries({ queryKey: key });
  }
}

// ---------------------------------------------------------------------------
// Tipagens
// ---------------------------------------------------------------------------
export interface TenantInvoice {
  id: string;
  source: "financeiro_cobrancas" | "billing_history";
  descricao: string | null;
  valor_total: number;
  status: string;
  data_emissao: string | null;
  data_vencimento: string | null;
  // v1.0.7 (2026-06-30) — data/hora de criação da cobrança (ordenação + coluna Dt. Criação)
  created_at: string | null;
  tipo_pagamento: string | null;
  tipo_cobranca: string | null;
  payment_link: string | null;
  invoice_url: string | null;
  provider_charge_id: string | null;
  conta_corrente_id: string | null;
  provider_slug: string | null;
  // v1.0.5 (2026-06-28) — paridade com ChargeProviderActions (link transparente + recibo).
  checkout_session_slug: string | null;
  checkout_mode: "transparent" | "provider" | null;
  receipt_url: string | null;
  ciclo_numero?: number | null;
  is_addon: boolean;
  // v1.0.11 (2026-07-03) — flags de cancelamento self-service
  is_prorated?: boolean;
  cancelable?: boolean;
}

export interface TenantInvoicesPage {
  source: "financeiro_cobrancas" | "billing_history";
  items: TenantInvoice[];
  total: number;
  paid_period: number;
  open_period: number;
  overdue_period: number;
}

export interface TenantAddonSubscription {
  id: string;
  assinatura_id: string;
  descricao: string | null;
  valor_total: number;
  moeda: string;
  frequencia: string | null;
  status: string | null;
  proximo_vencimento: string | null;
  created_at: string;
  notes: string | null;
  // v1.2.0: quantidade contratada e valor unitário recorrente
  quantity?: number;
  unit_price?: number;
  // v1.3.0: entitlement real (paid_quantity) e quantidade aguardando pagamento
  paid_quantity?: number;
  pending_quantity?: number;
}

export interface TenantAddonsList {
  items: TenantAddonSubscription[];
  count: number;
  total_value: number;
}

// ---------------------------------------------------------------------------
// Filtros de faturas
// ---------------------------------------------------------------------------
export interface TenantInvoiceFilters {
  status?: string | null;
  dateFrom?: string | null; // YYYY-MM-DD
  dateTo?: string | null;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// 1) Lista de faturas
// ---------------------------------------------------------------------------
export function useTenantInvoices(filters: TenantInvoiceFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ["tenant-billing-invoices", filters],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<TenantInvoicesPage> => {
      const { data, error } = await supabase.rpc(
        "tenant_billing_list_invoices" as never,
        {
          p_status: filters.status ?? null,
          p_date_from: filters.dateFrom ?? null,
          p_date_to: filters.dateTo ?? null,
          p_limit: filters.limit ?? 50,
          p_offset: filters.offset ?? 0,
        } as never,
      );
      if (error) {
        log.error("Erro listar faturas", { error });
        throw error;
      }
      const raw = (data ?? {}) as any;
      return {
        source: raw.source ?? "financeiro_cobrancas",
        items: Array.isArray(raw.items) ? (raw.items as TenantInvoice[]) : [],
        total: Number(raw.total ?? 0),
        paid_period: Number(raw.paid_period ?? 0),
        open_period: Number(raw.open_period ?? 0),
        overdue_period: Number(raw.overdue_period ?? 0),
      };
    },
  });
}

// ---------------------------------------------------------------------------
// 2) Lista de add-ons contratados
// ---------------------------------------------------------------------------
export function useTenantAddons(enabled = true) {
  return useQuery({
    queryKey: ["tenant-billing-addons"],
    enabled,
    staleTime: 0,
    // Add-ons contratados são baseline financeiro: ao montar a tela, nunca
    // devemos servir apenas cache antigo (o F5 não pode ser necessário).
    refetchOnMount: "always",
    queryFn: async (): Promise<TenantAddonsList> => {
      const { data, error } = await supabase.rpc(
        "tenant_billing_list_addons" as never,
        {} as never,
      );
      if (error) {
        log.error("Erro listar add-ons", { error });
        throw error;
      }
      const raw = (data ?? {}) as any;
      return {
        items: Array.isArray(raw.items) ? (raw.items as TenantAddonSubscription[]) : [],
        count: Number(raw.count ?? 0),
        total_value: Number(raw.total_value ?? 0),
      };
    },
  });
}

// ---------------------------------------------------------------------------
// 3) Cartões cadastrados vinculados à assinatura do tenant
// ---------------------------------------------------------------------------
/**
 * Representa um cartão tokenizado vinculado à assinatura do tenant.
 * `is_default` indica qual cartão será usado nas próximas cobranças recorrentes.
 */
export interface TenantSelfSavedCard {
  id: string;
  brand: string | null;
  last4: string;
  exp_month: number;
  exp_year: number;
  holder_name_masked: string | null;
  provider_slug: string;
  created_at: string;
  is_default: boolean;
}

/**
 * Lista cartões tokenizados da assinatura ativa do tenant (somente leitura).
 * Backend: `tenant_billing_get_self_saved_cards` v1.0.0.
 */
export function useTenantSelfSavedCards(enabled = true) {
  return useQuery({
    queryKey: ["tenant-self-saved-cards"],
    enabled,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<TenantSelfSavedCard[]> => {
      const { data, error } = await supabase.rpc(
        "tenant_billing_get_self_saved_cards" as never,
        {} as never,
      );
      if (error) {
        log.error("Erro listar cartões da assinatura", { error });
        throw error;
      }
      return ((data ?? []) as unknown) as TenantSelfSavedCard[];
    },
  });
}

// ---------------------------------------------------------------------------
// 4) Mutation: define cartão padrão da assinatura (self-service)
// ---------------------------------------------------------------------------
/**
 * Altera o cartão padrão da assinatura do tenant (usado nas próximas cobranças
 * recorrentes). Backend: `tenant_billing_set_self_default_card` v1.0.0 — faz
 * validação cross-tenant via `tenant_billing_bindings` e ownership do cartão.
 */
export function useTenantSetDefaultCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cardTokenId: string) => {
      const { data, error } = await supabase.rpc(
        "tenant_billing_set_self_default_card" as never,
        { p_card_token_id: cardTokenId } as never,
      );
      if (error) {
        log.error("Erro ao definir cartão padrão", { error });
        throw error;
      }
      return data as { success: boolean; changed: boolean };
    },
    onSuccess: () => {
      // Atualiza listagem de cartões e overview (que reflete o cartão padrão).
      qc.invalidateQueries({ queryKey: ["tenant-self-saved-cards"] });
      qc.invalidateQueries({ queryKey: ["tenant-billing-self-overview"] });
    },
  });
}

// ---------------------------------------------------------------------------
// 5) Memória de cálculo da cobrança (self-service, sanitizada)
// ---------------------------------------------------------------------------
/**
 * Busca a memória de cálculo de uma cobrança do tenant master, exposta de forma
 * sanitizada para o tenant CLIENTE (transparência + auditoria de pro-rata,
 * créditos, totais e timeline). Backend: `tenant_billing_get_self_calculation_memory`
 * v1.0.0 — valida o vínculo via `tenant_billing_bindings`, delega à RPC master
 * `get_cobranca_calculation_memory` v1.2.0 via GUC `app.wl_trusted_master_tenant`
 * e remove identificadores administrativos do master antes de retornar.
 */
export function useTenantSelfCalculationMemory(
  cobrancaId: string | null | undefined,
  enabled = true,
) {
  return useQuery<CobrancaMemoriaCalculo>({
    queryKey: ["tenant-self-calc-memory", cobrancaId],
    enabled: !!cobrancaId && enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "tenant_billing_get_self_calculation_memory" as never,
        { p_cobranca_id: cobrancaId as string } as never,
      );
      if (error) {
        log.error("Erro ao carregar memória de cálculo (self)", { error });
        throw error;
      }
      return data as unknown as CobrancaMemoriaCalculo;
    },
  });
}

// ---------------------------------------------------------------------------
// 6) Mutation: cancelar fatura pro-rata self-service
// ---------------------------------------------------------------------------
/**
 * Cancela uma cobrança PRO-RATA (add-on ou troca de plano ainda não paga)
 * pelo próprio tenant cliente. Faturas recorrentes normais são recusadas
 * pelo backend (`tenant_billing_cancel_self_prorata_charge` v1.0.0). O
 * trigger `tg_sync_tenant_billing_on_charge` cuida do rollback dos itens
 * via `discard_pending_plan_change` v1.1.0 quando o status transita para
 * `cancelado`.
 */
export function useTenantCancelSelfProrataCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cobrancaId,
      reason,
    }: {
      cobrancaId: string;
      reason?: string | null;
    }) => {
      const { data, error } = await supabase.rpc(
        "tenant_billing_cancel_self_prorata_charge" as never,
        {
          p_cobranca_id: cobrancaId,
          p_reason: reason ?? null,
        } as never,
      );
      if (error) {
        log.error("Erro ao cancelar cobrança pro-rata", { error });
        throw error;
      }
      return data as { success: boolean; self_service?: boolean };
    },
    onSuccess: () => {
      invalidateTenantBillingFreshness(qc);
    },
  });
}

